'use client';

import { useState } from 'react';
import type { BrokerConfig } from '@/config/brokers';

interface BrokerSelectionModalProps {
  brokers: BrokerConfig[];
  onSelect: (broker: BrokerConfig) => void;
  onClose: () => void;
}

const getBrokerIcon = (brokerId: string) => {
  const iconClass = "w-12 h-12 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0";
  const textClass = "text-white font-bold text-xl";
  
  switch (brokerId.toLowerCase()) {
    case 'zerodha':
      return (
        <div className={`${iconClass} bg-gradient-to-br from-orange-400 to-orange-600`}>
          <span className={textClass}>Z</span>
        </div>
      );
    case 'kotak':
      return (
        <div className={`${iconClass} bg-gradient-to-br from-red-500 to-red-700`}>
          <span className={textClass}>K</span>
        </div>
      );
    case 'bajaj':
      return (
        <div className={`${iconClass} bg-gradient-to-br from-blue-500 to-blue-700`}>
          <span className={textClass}>B</span>
        </div>
      );
    case 'mastertrust':
      return (
        <div className={`${iconClass} bg-gradient-to-br from-purple-500 to-purple-700`}>
          <span className={textClass}>M</span>
        </div>
      );
    case 'indmoney':
      return (
        <div className={`${iconClass} bg-gradient-to-br from-emerald-500 to-teal-600`}>
          <span className={textClass}>I</span>
        </div>
      );
    case 'axis':
      return (
        <div className={`${iconClass} bg-gradient-to-br from-rose-700 to-rose-900`}>
          <span className={textClass}>A</span>
        </div>
      );
    default:
      return (
        <div className={`${iconClass} bg-gradient-to-br from-zinc-400 to-zinc-600`}>
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
      );
  }
};

const getBrokerDescription = (brokerId: string): string => {
  switch (brokerId.toLowerCase()) {
    case 'zerodha':
      return 'India\'s largest stock broker';
    case 'kotak':
      return 'Kotak Securities trading platform';
    case 'bajaj':
      return 'Bajaj Finserv broker services';
    case 'mastertrust':
      return 'Master Trust trading solutions';
    case 'indmoney':
      return 'All-in-one investment platform';
    case 'axis':
      return 'Axis Direct RAPID API with SSO login';
    default:
      return 'Connect your trading account';
  }
};

export default function BrokerSelectionModal({
  brokers,
  onSelect,
  onClose,
}: BrokerSelectionModalProps) {
  const [hoveredBroker, setHoveredBroker] = useState<string | null>(null);

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-2xl w-full max-w-2xl max-h-[90dvh] overflow-y-auto transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative border-b border-zinc-200 dark:border-zinc-800 px-6 py-5">
          <div className="flex justify-between items-start">
            <div className="pr-8">
              <h3 className="text-xl font-bold tracking-tight text-zinc-900 dark:text-white">
                Add New Broker
              </h3>
              <p className="text-zinc-500 dark:text-zinc-400 text-sm mt-1">
                Select a broker to connect your trading account
              </p>
            </div>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 dark:hover:text-zinc-200 dark:hover:bg-zinc-800 rounded-full p-2 transition-colors duration-200"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Broker Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {brokers.map((broker) => (
              <button
                key={broker.id}
                onClick={() => onSelect(broker)}
                onMouseEnter={() => setHoveredBroker(broker.id)}
                onMouseLeave={() => setHoveredBroker(null)}
                className={`
                  group relative p-4 rounded-xl border transition-all duration-200 text-left
                  ${
                    hoveredBroker === broker.id
                      ? 'border-emerald-500 bg-emerald-500/5 shadow-lg'
                      : 'border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-emerald-500/40 hover:shadow-md'
                  }
                `}
              >
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  {getBrokerIcon(broker.id)}

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base font-semibold text-zinc-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors truncate">
                      {broker.displayName}
                    </h4>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5 truncate">
                      {getBrokerDescription(broker.id)}
                    </p>
                  </div>

                  {/* Arrow */}
                  <div className={`
                    flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200
                    ${hoveredBroker === broker.id
                      ? 'bg-emerald-500 text-white'
                      : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-400 group-hover:bg-emerald-500/10 group-hover:text-emerald-500'
                    }
                  `}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-800 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-emerald-500/10 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h5 className="text-sm font-semibold text-zinc-900 dark:text-white">
                  Secure Connection
                </h5>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
                  Your credentials are encrypted and stored securely. We never share your data with third parties.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-zinc-50 dark:bg-zinc-800/60 border-t border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center justify-between">
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Need help? Contact support for assistance
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
